import * as React from 'react';
import * as _ from 'underscore';
import { SiteUrls } from '@tableau/shared-urls';
import { Formatters, Messages } from '../../codegen/Localize';
import { IUserNotification } from '../models/IUserNotification';
import { appStore } from '../../app/AppStore';
import { NotificationCenterStyles } from '../styles/notificationCenterStyles';
import { NotificationPanelHeader } from './NotificationPanelHeader';
import { imgPath } from '../../common/utilities/ImgUrl';
import { TabActivationMode, TabAndPanelContents, TabListAlignment, TabsWidget } from '@tableau/widgets';
import { Notification } from './Notification';
import { Keys } from '../../ts/helpers/Keys';
import * as notificationStyles from '../styles/notificationStyles';
import { Location } from '../../common/react/Location';
import { NotificationProviders } from '../contentProviders/NotificationProviders';
import { UserNotificationActions } from '../redux/UserNotificationActions';
import { IUserNotificationContentProvider } from '../contentProviders/INotificationProvider';
import { UserNotificationType } from '../contentProviders/UserNotificationType';
import { NotificationLoader } from './NotificationLoader';

//This state is used to control when to show the loading and failure state
export enum NotificationDisplayMode {
  NotificationLoaded,
  NotificationLoading,
  NotificationLoadFailed
}

export type NotificationPanelProps = {
  siteUrls: SiteUrls;
  userNotificationActions: UserNotificationActions;
  displayMode: NotificationDisplayMode;
};

export type NotificationPanelState = {
  notifications: _.Dictionary<IUserNotification>;
  unreadNotifications: _.Dictionary<IUserNotification>;
};

export enum NotificationStatus {
  READ,
  UNREAD,
  ALL
}


export class NotificationPanel extends React.PureComponent<NotificationPanelProps, NotificationPanelState> {

  private unsubscribeFromAppStore: () => void;
  private notificationProviders: _.Dictionary<IUserNotificationContentProvider>;
  private tabsDimensionOverrides = {
    tabHeight: 32,
    tabPanelPadding: '0'
  };

  constructor(props: NotificationPanelProps) {
    super(props);
    this.notificationProviders = NotificationProviders(this.props.userNotificationActions);
    this.state = {
      notifications: {},
      unreadNotifications: {}
    };
    this.renderNotifications = this.renderNotifications.bind(this);
    this.onAppStoreChange = this.onAppStoreChange.bind(this);
    this.onTabChange = this.onTabChange.bind(this);
  }

  public componentDidMount() {
    this.unsubscribeFromAppStore = appStore.subscribe(this.onAppStoreChange);
  }

  public componentWillUnmount() {
    this.unsubscribeFromAppStore();
  }

  private onAppStoreChange() {
    const { unreadNotifications, notifications } = appStore.getState().notificationState;
    this.setState({ notifications, unreadNotifications });
  }


  private onTabChange(tabIndex: number): void {
    this.props.userNotificationActions.updateUnreadUserNotifications();
  }

  public render() {
    return <div id={'notification-panel'} role={'dialog'} data-tb-test-id='notification-panel' style={NotificationCenterStyles.notificationContainerStyle}>
      <NotificationPanelHeader
        userPreferenceUrl="localhost"
        isMarkAllReadDisabled={!this.state.notifications}
        userNotificationActions={this.props.userNotificationActions}
        userNotifications={this.state.notifications}
        displayMode={this.props.displayMode} />
      {this.props.displayMode === NotificationDisplayMode.NotificationLoading ? <NotificationLoader /> :
        <div className={NotificationCenterStyles.notificationPanelTab}>
          <TabsWidget
            activationMode={TabActivationMode.Manual}
            anchorTabIndex={0}
            ariaLabel={'notification tab'}
            initialActiveIndex={0}
            tabListAlignment={TabListAlignment.Left}
            tabs={this.getNotificationTabs()}
            dimensionOverrides={this.tabsDimensionOverrides}
            onActiveIndexChanged={this.onTabChange}
            testId={'notification-center-tab'}
          />
        </div>
      }
    </div>;
  }
  // render empty panel, localization pending
  private renderEmptyNotification(tabState: NotificationStatus) {
    return (
      <div style={NotificationCenterStyles.notificationEmptyList}>
        <div> <img src={imgPath('notification_empty_bell.svg')} alt={'user preference'} height="80" /></div>
        <div style={NotificationCenterStyles.notificationEmptyTitle}> You have no notifications </div>
        {
          tabState === NotificationStatus.ALL && <div style={NotificationCenterStyles.notificationEmptySubtitle}>Your important status changes will appear here. </div>
        }
      </div>);
  }

  //TODO: localization
  private getNotificationTabs(): TabAndPanelContents[] {
    return [{
      panelContent: this.renderNotifications(NotificationStatus.ALL),
      tabContent: { label: 'All', title: 'All notifications' }
    }, {
      panelContent: this.renderNotifications(NotificationStatus.UNREAD),
      tabContent: { label: 'Unread', title: 'Unread notifications' }
    }];
  }

  private renderNotifications(tabState: NotificationStatus): JSX.Element {
    let notifications = tabState === NotificationStatus.ALL ? this.state.notifications : this.state.unreadNotifications;

    if (!notifications || _.keys(notifications).length === 0) {
      return this.renderEmptyNotification(tabState);
    }
    return (
      <div role='grid' data-tb-test-id={`notification-tab-${tabState}`} style={NotificationCenterStyles.notificationTabContent}>
        {
          _.values(notifications).map((notification, index) => {
            if (Object.values(UserNotificationType).includes(notification.type)){
              return <Notification
                key={index}
                id={notification.id}
                notificationType={notification.type}
                displayName={notification.senderDisplayName}
                profileImageUrl={notification.profileImageUrl}
                createdAt={notification.creationTime}
                isRead={notification.read}
                onKeyDown={this.handleKeyDown}
                onNotificationClick={() => this.onNotificationClick(notification)}
                title={this.notificationProviders[notification.type].getNotificationTitle(notification)}
                content={this.notificationProviders[notification.type].getNotificationContent(notification)}
                notificationActions={this.notificationProviders[notification.type].getNotificationActions(notification)}
              />;
          }})
        }
      </div>
    );
  }

  private onNotificationClick(notification: IUserNotification) {
    if (!notification.read) {
      this.props.userNotificationActions.updateUserNotificationsStatus([notification.id], true);
    }
    Location.instance.setUrl(notification.targetUrl);
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = e => {

    if (e.keyCode === Keys.ArrowDown || e.keyCode === Keys.ArrowUp) {
      e.preventDefault();
      e.stopPropagation();
      const selector = '.' + notificationStyles.notificationItem;
      const notifications = e.currentTarget.parentElement.parentElement.querySelectorAll<HTMLDivElement>(selector);
      let index: number = Array.prototype.indexOf.call(notifications, e.target);
      if (e.keyCode === Keys.ArrowDown) {
        index = Math.min(notifications.length - 1, index + 1);
      } else if (e.keyCode === Keys.ArrowUp) {
        index = Math.max(0, index - 1);
      }
      notifications[index].focus();
    } else if (e.keyCode === Keys.ArrowRight) {
      e.preventDefault();
      e.stopPropagation();
      const actionButton = e.currentTarget.nextElementSibling.querySelector<HTMLDivElement>('button');
      actionButton?.focus();
    }
  }
}